/**
 * 장소명 다국어 필터링
 *
 * 사용자가 등록한 원본 문자열이 여러 언어를 혼합하고 있을 때
 * (예: "Kyukamura Shikanoshima 休暇村 志賀島", "Saitozaki Seaside Country Club 西戸崎シーサイド カントリークラブ 골프장"),
 * 현재 설정된 언어(ko/ja/en) 스크립트에 해당하는 부분만 남긴다.
 *
 * 규칙:
 *   - ko: 한글(완성형·자모) 토큰만 유지
 *   - ja: 히라가나·가타카나·한자(CJK Unified Ideographs) 토큰만 유지
 *   - en: 라틴 알파벳(공백 포함) 토큰만 유지
 *
 * 각 토큰은 공백으로 분리해서 판별하며, 대상 언어 토큰이 하나도 없으면
 * 원본 그대로 반환한다(예: "인천국제공항"을 en 로 보면 → 원본 유지).
 */

type Lang = 'ko' | 'ja' | 'en';

const isKorean = (ch: string): boolean => {
    const c = ch.charCodeAt(0);
    return (c >= 0xAC00 && c <= 0xD7A3) || (c >= 0x3130 && c <= 0x318F) || (c >= 0x1100 && c <= 0x11FF);
};

const isJapanese = (ch: string): boolean => {
    const c = ch.charCodeAt(0);
    // 히라가나 3040-309F, 가타카나 30A0-30FF, 반각 가타카나 FF66-FF9F, CJK 통합한자 4E00-9FFF, CJK 확장 3400-4DBF
    return (
        (c >= 0x3040 && c <= 0x309F) ||
        (c >= 0x30A0 && c <= 0x30FF) ||
        (c >= 0xFF66 && c <= 0xFF9F) ||
        (c >= 0x4E00 && c <= 0x9FFF) ||
        (c >= 0x3400 && c <= 0x4DBF)
    );
};

const isLatin = (ch: string): boolean => {
    const c = ch.charCodeAt(0);
    // 라틴 알파벳 + 확장 + 숫자
    return (
        (c >= 0x41 && c <= 0x5A) ||
        (c >= 0x61 && c <= 0x7A) ||
        (c >= 0x30 && c <= 0x39) ||
        (c >= 0xC0 && c <= 0xFF && c !== 0xD7 && c !== 0xF7)
    );
};

const tokenMatchesLang = (token: string, lang: Lang): boolean => {
    // 토큰 내 문자 중 대상 언어 스크립트가 하나라도 있으면 match
    for (const ch of token) {
        if (lang === 'ko' && isKorean(ch)) return true;
        if (lang === 'ja' && isJapanese(ch)) return true;
        if (lang === 'en' && isLatin(ch)) return true;
    }
    return false;
};

const tokenIsNeutral = (token: string): boolean => {
    // 숫자/기호/구분자만 있는 토큰(예: "07:00", "-", "・") → 대상 언어와 무관하게 유지 대상 후보
    // 하지만 en 에는 숫자가 포함되므로 en 일 때만 유지, 그 외엔 뺀다.
    for (const ch of token) {
        if (isKorean(ch) || isJapanese(ch) || isLatin(ch)) return false;
    }
    return true;
};

export const localizePlaceName = (name: string | undefined | null, lang: string | undefined | null): string => {
    if (!name) return '';
    const l = (lang || 'ko').slice(0, 2).toLowerCase() as Lang;
    if (l !== 'ko' && l !== 'ja' && l !== 'en') return name;

    const tokens = name.split(/(\s+)/); // 공백 그룹도 보존
    const kept: string[] = [];
    let hasLangToken = false;
    for (const tk of tokens) {
        if (/^\s+$/.test(tk)) { kept.push(tk); continue; }
        if (tokenMatchesLang(tk, l)) {
            kept.push(tk);
            hasLangToken = true;
        }
    }
    if (!hasLangToken) return name; // 대상 언어 토큰 전무 → 원본 유지 (예: "인천국제공항"을 en 요청)

    // 앞뒤 공백 정리 + 중간 연속 공백 압축
    return kept.join('').replace(/\s+/g, ' ').trim();
};
